import { createSelector } from "@reduxjs/toolkit"
import { isCrossChannel } from "@root/store/utils"

import { RootState } from "."
import {
  AnalyticsData,
  ChannelData,
  ContactData,
  Debug,
  isRequiredChannelData,
  Metadata,
  PreferenceCenterMetadata,
} from "./types"

/**
 * Debug
 */
const debugSlice = (state: RootState): Debug => state.debug

export const selectDebugInfo = createSelector(
  debugSlice,
  ({ debugInfo = null }) => debugInfo
)

/**
 * Metadata
 */
const metadataSlice = (state: RootState): Metadata => state.metadata

export const selectLocaleOverride = createSelector(
  metadataSlice,
  ({ localeOverride }) => localeOverride
)

export const selectPreferenceCenters = createSelector(
  metadataSlice,
  ({ preferenceCenters = [] }) => preferenceCenters
)

export const selectPreferenceCenterMetadata = createSelector(
  metadataSlice,
  ({ preferenceCenters = [] }): PreferenceCenterMetadata[] => {
    return preferenceCenters.map((pref) => ({
      id: pref.id,
      name: pref.display?.name,
      type: isCrossChannel(pref) ? "contact" : "channel",
    }))
  }
)

export const selectSupportInfo = createSelector(
  metadataSlice,
  ({ supportInfo }) => supportInfo
)

/**
 * Contact
 */
const contactSlice = (state: RootState): ContactData => state.contact

export const selectContact = createSelector(contactSlice, (slice) => slice)
export const selectContactAssociatedChannels = createSelector(
  contactSlice,
  (slice) => slice.associated
)
export const selectContactTags = createSelector(
  contactSlice,
  (slice) => slice.tags
)
export const selectContactSubscriptions = createSelector(
  contactSlice,
  (slice) => slice.subscription_lists
)
export const selectContactAttributes = createSelector(
  contactSlice,
  (slice) => slice.attributes
)

/**
 * Channel
 */
const channelSlice = (state: RootState): ChannelData => state.channel

export const selectChannel = createSelector(
  channelSlice,
  (slice): Required<ChannelData> | null => {
    if (isRequiredChannelData(slice)) {
      return slice
    }

    return null
  }
)

export const selectChannelTags = createSelector(
  channelSlice,
  (slice) => slice.tags
)

export const isOptedIn = createSelector(
  channelSlice,
  (slice) => Boolean(slice.id) && slice.optIn
)

export const selectChannelAttributes = createSelector(
  channelSlice,
  (slice) => slice.attributes
)

export const selectChannelSubscriptions = createSelector(
  channelSlice,
  (slice) => slice.subscriptions
)

/**
 * Analytics
 */
const analyticsSlice = (state: RootState): AnalyticsData => state.analytics

export const selectAnalytics = createSelector(analyticsSlice, (slice) => slice)

export const selectAnalyticsAssociatedIdentifiers = createSelector(
  analyticsSlice,
  (slice) => slice.associatedIdentifiers
)
